import { Button, cn, EmptyState, inputBaseClass } from '@paguz/ui';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  LinkIcon,
  SearchXIcon,
  ShieldAlertIcon,
  ShieldCheckIcon,
} from 'lucide-react';
import { useState } from 'react';
import { describeError } from '../../api/client.ts';
import { api, queryKeys } from '../../api/endpoints.ts';
import type { AuditSearch } from '../../app/search.ts';
import { Field, compactInputClass } from '../../components/form-controls.tsx';
import { ErrorState, LoadingRows, PageHeader, Panel } from '../../components/page.tsx';
import { formatDateTime } from '../../lib/format.ts';
import { AuditTable } from './audit-table.tsx';

const route = getRouteApi('/app/auditoria');

export function AuditPage() {
  const search = route.useSearch();
  const navigate = route.useNavigate();
  const [stack, setStack] = useState<string[]>([]);
  const query = useQuery({
    queryKey: queryKeys.audit(search),
    queryFn: () => api.listAudit(search),
    placeholderData: keepPreviousData,
  });

  return (
    <>
      <PageHeader
        title="Auditoría"
        description="Quién hizo qué, cuándo, desde dónde y por qué. Registros inmutables encadenados por hash."
      />
      <div className="grid gap-6 2xl:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="flex min-w-0 flex-col gap-5">
          <Filters
            key={JSON.stringify(search)}
            initial={search}
            onApply={(next) => {
              setStack([]);
              void navigate({ search: { ...next, cursor: undefined } });
            }}
          />
          <Panel bodyClassName="p-0">
            {query.isPending ? (
              <div className="p-5">
                <LoadingRows />
              </div>
            ) : query.isError ? (
              <div className="p-5">
                <ErrorState error={query.error} onRetry={() => void query.refetch()} />
              </div>
            ) : query.data.data.length === 0 ? (
              <div className="p-5">
                <EmptyState
                  icon={<SearchXIcon />}
                  title="Sin registros"
                  description="Ajusta los filtros de búsqueda."
                />
              </div>
            ) : (
              <>
                <div className={cn(query.isPlaceholderData && 'opacity-60')}>
                  <AuditTable entries={query.data.data} />
                </div>
                <div className="flex items-center justify-between border-t border-border px-5 py-3 text-sm text-muted-foreground">
                  <span>Clic en un registro para ver antes/después y hash.</span>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={stack.length === 0}
                      onClick={() => {
                        const prev = stack.slice(0, -1);
                        setStack(prev);
                        void navigate({ search: { ...search, cursor: prev.at(-1) } });
                      }}
                    >
                      <ChevronLeftIcon /> Anterior
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={!query.data.nextCursor}
                      onClick={() => {
                        const next = query.data.nextCursor;
                        if (!next) return;
                        setStack([...stack, next]);
                        void navigate({ search: { ...search, cursor: next } });
                      }}
                    >
                      Siguiente <ChevronRightIcon />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </Panel>
        </div>
        <VerifyPanel />
      </div>
    </>
  );
}

function Filters({
  initial,
  onApply,
}: {
  initial: AuditSearch;
  onApply: (next: AuditSearch) => void;
}) {
  const [draft, setDraft] = useState<AuditSearch>(initial);
  const input = cn(inputBaseClass, compactInputClass);
  const field = (key: keyof AuditSearch, label: string, placeholder?: string, type = 'text') => (
    <Field label={label} htmlFor={`a-${key}`}>
      <input
        id={`a-${key}`}
        type={type}
        className={input}
        placeholder={placeholder}
        value={draft[key] ?? ''}
        onChange={(e) => {
          setDraft((d) => ({ ...d, [key]: e.target.value === '' ? undefined : e.target.value }));
        }}
      />
    </Field>
  );
  return (
    <form
      aria-label="Filtros de auditoría"
      className="grid gap-3 rounded-2xl border border-border bg-card p-4 md:grid-cols-3 2xl:grid-cols-6"
      onSubmit={(e) => {
        e.preventDefault();
        onApply(draft);
      }}
    >
      {field('actor', 'Actor', 'usuario, cliente, agente…')}
      {field('action', 'Acción', 'config., payment.…')}
      {field('entity', 'Entidad', 'pi_…, credit_line…')}
      {field('traceId', 'traceId')}
      {field('from', 'Desde', undefined, 'date')}
      {field('to', 'Hasta', undefined, 'date')}
      <div className="flex gap-2 md:col-span-3 2xl:col-span-6">
        <Button type="submit" variant="highlight" className="h-10">
          Buscar
        </Button>
        <Button
          type="button"
          variant="ghost"
          className="h-10"
          onClick={() => {
            setDraft({});
            onApply({});
          }}
        >
          Limpiar
        </Button>
      </div>
    </form>
  );
}

function VerifyPanel() {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const verify = useMutation({ mutationFn: () => api.verifyAudit(from || null, to || null) });
  const result = verify.data;
  return (
    <Panel
      title={
        <span className="flex items-center gap-2">
          <LinkIcon className="size-5 text-highlight" /> Cadena de hashes
        </span>
      }
      description="Recalcula el hash de cada registro del rango y lo compara con el siguiente."
      className="order-first 2xl:order-none 2xl:self-start"
    >
      <form
        className="grid items-end gap-3 sm:grid-cols-[minmax(0,1fr)_auto] 2xl:grid-cols-1"
        onSubmit={(e) => {
          e.preventDefault();
          verify.mutate();
        }}
      >
        <div className="grid grid-cols-2 gap-3 2xl:grid-cols-2">
          <Field label="Desde" htmlFor="v-from">
            <input
              id="v-from"
              type="date"
              className={cn(inputBaseClass, compactInputClass)}
              value={from}
              onChange={(e) => {
                setFrom(e.target.value);
              }}
            />
          </Field>
          <Field label="Hasta" htmlFor="v-to">
            <input
              id="v-to"
              type="date"
              className={cn(inputBaseClass, compactInputClass)}
              value={to}
              onChange={(e) => {
                setTo(e.target.value);
              }}
            />
          </Field>
        </div>
        <Button type="submit" variant="highlight" loading={verify.isPending}>
          <ShieldCheckIcon /> Verificar cadena
        </Button>
      </form>
      {verify.isError && (
        <p
          role="alert"
          className="mt-4 rounded-lg bg-danger-soft px-3 py-2 text-sm text-danger-soft-foreground"
        >
          {describeError(verify.error)}
        </p>
      )}
      {result && (
        <div
          role="status"
          className={cn(
            'mt-4 flex gap-3 rounded-xl px-4 py-3 text-sm',
            result.status === 'INTACT'
              ? 'bg-success-soft text-success-soft-foreground'
              : 'bg-danger-soft text-danger-soft-foreground',
          )}
        >
          {result.status === 'INTACT' ? (
            <ShieldCheckIcon className="size-5 shrink-0" />
          ) : (
            <ShieldAlertIcon className="size-5 shrink-0" />
          )}
          <div>
            <p className="font-semibold">
              {result.status === 'INTACT' ? 'Cadena íntegra' : 'Cadena rota'}
            </p>
            <p>
              {result.checked} registros verificados
              {result.from || result.to
                ? ` (${result.from ? formatDateTime(result.from) : 'inicio'} – ${result.to ? formatDateTime(result.to) : 'hoy'})`
                : ' (todo el historial)'}
              .
            </p>
            {result.firstInvalid && (
              <p className="mt-1">
                Primer registro inconsistente: #{result.firstInvalid.seq} del{' '}
                {formatDateTime(result.firstInvalid.at)}.
              </p>
            )}
          </div>
        </div>
      )}
    </Panel>
  );
}
